import React, { memo } from 'react';

import { Title } from '../../../components/Title/Title';
import { ShortGameDescription } from '../../../components/ShortGameDescription/ShortGameDescription';
import { ITranslateMethod } from 'js/hooks/i18n/types';
import { RoundIcon } from '../../../components/RoundIcon';
import { CategoryName } from '../CategoryName';
import { useDeviceSelector } from 'js/store/hooks';
import { pick } from 'ramda';
import { clsx } from 'js/utils/classNames.util';
import { FullDescription } from '../FullDescription';

import style from './GameDetailsWrapper.scss';
import { withUnmountAnimation } from '@theme/js/hocs/withUnmountAnimation';

import { preventSwipeOnScroll } from '../../../utils';

type GameDetailsWrapper = {
    name: string;
    t: ITranslateMethod;
    categoryName?: string;
    isRecommended?: boolean;
    mostPlayedGame?: string;
    username?: string;
    isBecauseYouPlayed?: boolean;
    isCategoryName?: boolean;
    isNew?: boolean;
    isPopular?: boolean;
    isHot?: boolean;
    isExclusive?: boolean;
    shortDescription?: string;
    fullDescription?: string;
    showGameDetails?: boolean;
    handleShowGameDetails: () => void;
    descriptionGameProvider?: string;
    code: string;
}

const CategoryNameWithUnmountAnimation = withUnmountAnimation(CategoryName);
const FullDescriptionWithUnmountAnimation = withUnmountAnimation(FullDescription);
const CloseButtonWithUnmountAnimation = withUnmountAnimation(RoundIcon);

const GameDetailsWrapper = ({
    t,
    categoryName = '',
    isRecommended,
    mostPlayedGame,
    username,
    name,
    isBecauseYouPlayed = false,
    isCategoryName = false,
    isNew = false,
    isPopular = false,
    isHot = false,
    isExclusive = false,
    shortDescription,
    fullDescription,
    showGameDetails = false,
    handleShowGameDetails,
    descriptionGameProvider,
    code
}: GameDetailsWrapper) => {
    const tkLabelPrefix = 'playtech.mobile.system.responsivegames.game.label.';

    const { isPortrait } = useDeviceSelector(
        pick(['isPortrait'])
    );

    const hideCategoryName = showGameDetails && isPortrait;

    return (
        <div
            onTouchStart={preventSwipeOnScroll}
            className={clsx([
                style.gameDetailsWrapper,
                showGameDetails ? style.gameDetailsWrapperShowDetails : ''
            ])}
        >
           <CategoryNameWithUnmountAnimation
                animationDuration={500}
                animationClass={style.categoryNameRegularFadeOut}
                shouldRender={!hideCategoryName}
                className={clsx([
                    style.categoryNameRegular,
                    showGameDetails ? style.categoryName : ''
                ])}
                t={t}
                categoryName={categoryName}
                isRecommended={isRecommended}
                mostPlayedGame={mostPlayedGame}
                username={username}
                isBecauseYouPlayed={isBecauseYouPlayed}
                isCategoryName={isCategoryName}
            />

            <div className={`${style.gameTitleContainer} ${showGameDetails ? style.gameTitleContainerShowDetails : ''}`}>
                <div className={style.titleAndLabels}>
                    <Title className={clsx([
                        style.gameTitle,
                        showGameDetails ? style.gameTitleShowDetails : ''
                    ])}>{name}</Title>
                    {<div className={clsx([style.labelContainer, !categoryName ? style.withoutCategory : ''])}>
                        {isNew && <span className={style.label}>{t(`${tkLabelPrefix}new`)}</span>}
                        {isPopular && <span className={style.label}>{t(`${tkLabelPrefix}popular`)}</span>}
                        {isHot && <span className={style.label}>{t(`${tkLabelPrefix}hot`)}</span>}
                        {isExclusive && <span className={style.label}>{t(`${tkLabelPrefix}exclusive`)}</span>}
                    </div>}
                </div>
                <CloseButtonWithUnmountAnimation
                    containerClassName={style.closeIconContainer}
                    className={style.closeIcon}
                    type="close"
                    onClick={handleShowGameDetails}
                    shouldRender={showGameDetails}
                    animationClass={style.closeUnmount}
                    animationDuration={500}
                    animationClassesObject={{
                        containerClassName: clsx([style.closeIconContainer, style.closeUnmountContainer])
                    }}
                />
            </div>
            {!showGameDetails && shortDescription && (
                <ShortGameDescription
                    className={style.shortDescription}
                    isDangerouslySetInnerHTML
                    description={shortDescription}
                />
            )}
            <FullDescriptionWithUnmountAnimation
                shouldRender={showGameDetails}
                t={t}
                code={code}
                className={style.fullDescription}
                animationClass={style.fullDescription}
                animationDuration={200}
                fullDescription={fullDescription}
                descriptionGameProvider={descriptionGameProvider}
            />
        </div>
    );
};

export default memo(GameDetailsWrapper);
